import { For, Show, createMemo, createSignal, onMount } from 'solid-js'
import { useNavigate } from '@solidjs/router'
import { useAuth } from '../lib/auth'
import { getCheckIns } from '../lib/api'

/**
 * Items & activity — the guest's own record of everything they have handed over.
 *
 * WHY A SEPARATE SCREEN: the hub's card showed a summary (venue, date, count,
 * price) and the history page repeated roughly the same thin row. Neither could
 * answer the questions a guest actually has at a counter or after a visit:
 *
 *   - what exactly did I check in, and under which tag?
 *   - what did it cost, and what part of that was a voucher?
 *   - when did I hand it over, and when did I get it back?
 *   - which items are still there right now?
 *
 * So this screen is organised by VISIT, expanded by default, with every item
 * listed individually: its type, its tag, and its own state and timestamps.
 * Everything is read from the existing check-ins endpoint — no new data source.
 */

interface Item {
  id: string
  type: string
  tagNumber: string | number | null
  status: string
  returnedAt?: string | null
}

interface PendingRequest {
  id: string
  venueName: string
  totalAmount: number
  itemCount: number
  createdAt: string
}

interface Visit {
  id: string
  venueId: string
  venueName: string
  items: Item[]
  totalAmount: number
  status: string
  createdAt: string
  checkedOutAt: string | null
}

export default function Activity() {
  const navigate = useNavigate()
  const auth = useAuth()

  const [visits, setVisits] = createSignal<Visit[]>([])
  const [pending, setPending] = createSignal<PendingRequest[]>([])
  const [isLoading, setIsLoading] = createSignal(true)
  const [showAll, setShowAll] = createSignal(false)
  const [error, setError] = createSignal<string | null>(null)

  onMount(async () => {
    if (!auth.isAuthenticated()) {
      navigate('/signup')
      return
    }
    await load()
  })

  async function load() {
    setIsLoading(true)
    const res = await getCheckIns()
    if (res.error) {
      setError(res.error.message)
    } else {
      // `res.data` is typed by lib/api; narrow through unknown so a shape change
      // fails here loudly instead of rendering an empty screen.
      const d = (res.data ?? {}) as { checkIns?: Visit[]; pending?: PendingRequest[] }
      setVisits(Array.isArray(d.checkIns) ? d.checkIns : [])
      setPending(Array.isArray(d.pending) ? d.pending : [])
      setError(null)
    }
    setIsLoading(false)
  }

  const shown = () => (showAll() ? visits() : visits().slice(0, 8))

  /** Everything the guest has ever handed over — the headline number. */
  const stats = createMemo(() => {
    const all = visits().flatMap((v) => v.items ?? [])
    return {
      visits: visits().length,
      items: all.length,
      returned: all.filter((i) => i.status === 'checked_out' || i.status === 'returned').length,
      held: all.filter((i) => i.status === 'checked_in').length,
      spent: visits().reduce((sum, v) => sum + (v.totalAmount ?? 0), 0),
    }
  })

  const euros = (cents: number | null | undefined) => `€${((cents ?? 0) / 100).toFixed(2)}`

  const day = (iso: string | null | undefined) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  }

  const clock = (iso: string | null | undefined) => {
    if (!iso) return ''
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  }

  /** "1 h 20 min" between two timestamps — how long the item was held. */
  const duration = (from: string, to: string | null | undefined) => {
    if (!to) return ''
    const a = new Date(from).getTime()
    const b = new Date(to).getTime()
    if (Number.isNaN(a) || Number.isNaN(b) || b < a) return ''
    const mins = Math.floor((b - a) / 60000)
    if (mins < 60) return `${mins} min`
    const h = Math.floor(mins / 60)
    if (h < 24) return `${h} h ${mins % 60} min`
    return `${Math.floor(h / 24)} d`
  }

  const isReturned = (i: Item) => i.status === 'checked_out' || i.status === 'returned'

  return (
    <div class="min-h-dvh bg-gray-50 flex flex-col">
      <header class="bg-white border-b border-gray-200 px-4 py-3">
        <div class="max-w-md mx-auto flex items-center justify-between">
          <div class="flex items-center gap-2">
            <button onClick={() => navigate('/pass')} class="text-gray-500 hover:text-gray-700" aria-label="Back">
              ←
            </button>
            <span class="font-semibold text-gray-900">Your items & activity</span>
          </div>
        </div>
      </header>

      <main class="flex-1 p-4">
        <div class="max-w-md mx-auto">
          <Show when={!isLoading()} fallback={<p class="py-12 text-center text-gray-500">Loading…</p>}>
            <Show when={error()}>
              <div class="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error()}
              </div>
            </Show>

            {/* Summary — the four numbers that answer "where do I stand?". */}
            <section class="mb-4 rounded-xl border border-gray-200 bg-white p-4">
              <div class="grid grid-cols-2 gap-3 text-center">
                <div>
                  <p class="text-2xl font-bold text-gray-900">{stats().held}</p>
                  <p class="text-[11px] text-gray-500">with the venue now</p>
                </div>
                <div>
                  <p class="text-2xl font-bold text-gray-900">{stats().returned}</p>
                  <p class="text-[11px] text-gray-500">returned to you</p>
                </div>
                <div>
                  <p class="text-2xl font-bold text-gray-900">{stats().visits}</p>
                  <p class="text-[11px] text-gray-500">visits</p>
                </div>
                <div>
                  <p class="text-2xl font-bold text-gray-900">{euros(stats().spent)}</p>
                  <p class="text-[11px] text-gray-500">total spent</p>
                </div>
              </div>
            </section>

            {/* Waiting requests belong here too: a guest looking for "my items"
                must see one they have not handed over yet. */}
            <Show when={pending().length > 0}>
              <section class="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <h2 class="mb-2 text-sm font-semibold text-amber-900">Waiting at the counter</h2>
                <For each={pending()}>
                  {(p) => (
                    <div class="flex items-center justify-between py-1 text-sm">
                      <span class="text-amber-900">
                        {p.venueName} · {p.itemCount} item{p.itemCount === 1 ? '' : 's'}
                      </span>
                      <span class="font-medium text-amber-900">{euros(p.totalAmount)}</span>
                    </div>
                  )}
                </For>
              </section>
            </Show>

            <Show
              when={visits().length > 0}
              fallback={
                <section class="rounded-xl border border-gray-200 bg-white p-8 text-center">
                  <p class="mb-1 text-gray-600">Nothing checked in yet</p>
                  <p class="text-sm text-gray-400">Hand something to a cloakroom and it will appear here.</p>
                </section>
              }
            >
              <div class="space-y-3">
                <For each={shown()}>
                  {(visit) => {
                    const total = visit.items?.length ?? 0
                    const back = (visit.items ?? []).filter(isReturned).length
                    return (
                      <section class="rounded-xl border border-gray-200 bg-white p-4">
                        <div class="flex items-start justify-between">
                          <div class="min-w-0">
                            <h3 class="truncate text-sm font-semibold text-gray-900">{visit.venueName}</h3>
                            <p class="text-xs text-gray-500">
                              {day(visit.createdAt)} · {clock(visit.createdAt)}
                            </p>
                          </div>
                          <div class="ml-3 shrink-0 text-right">
                            <p class="text-sm font-semibold text-gray-900">{euros(visit.totalAmount)}</p>
                            <span
                              class={`text-[11px] ${back === total ? 'text-gray-400' : 'text-emerald-600'}`}
                            >
                              {back === total ? 'all returned' : `${back}/${total} returned`}
                            </span>
                          </div>
                        </div>

                        <ul class="mt-3 space-y-1.5">
                          <For each={visit.items ?? []}>
                            {(item) => (
                              <li class="rounded-lg bg-gray-50 px-3 py-2">
                                <div class="flex items-center justify-between">
                                  <span class="text-sm capitalize text-gray-800">{item.type}</span>
                                  <span class="font-mono text-xs text-gray-500">#{item.tagNumber ?? '—'}</span>
                                </div>
                                <div class="mt-0.5 flex items-center justify-between text-[11px]">
                                  <Show
                                    when={isReturned(item)}
                                    fallback={<span class="text-emerald-600">Still with the venue</span>}
                                  >
                                    <span class="text-gray-400">
                                      Returned
                                      <Show when={item.returnedAt}>
                                        {' '}
                                        {clock(item.returnedAt)}
                                        <Show when={duration(visit.createdAt, item.returnedAt)}>
                                          {' '}
                                          · held {duration(visit.createdAt, item.returnedAt)}
                                        </Show>
                                      </Show>
                                    </span>
                                  </Show>
                                  <Show when={!isReturned(item)}>
                                    <span class="text-gray-400">in since {clock(visit.createdAt)}</span>
                                  </Show>
                                </div>
                              </li>
                            )}
                          </For>
                        </ul>

                        <Show when={back === total && visit.checkedOutAt}>
                          <p class="mt-2 text-[11px] text-gray-400">
                            Closed {day(visit.checkedOutAt)} · {clock(visit.checkedOutAt)}
                          </p>
                        </Show>
                      </section>
                    )
                  }}
                </For>
              </div>

              <Show when={visits().length > 8 && !showAll()}>
                <button
                  onClick={() => setShowAll(true)}
                  class="mt-3 w-full rounded-lg border border-gray-300 bg-white py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Show all {visits().length} visits
                </button>
              </Show>
            </Show>
          </Show>
        </div>
      </main>
    </div>
  )
}
